import { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    'beforeinstallprompt': BeforeInstallPromptEvent;
  }
}

function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone === true;
    
    if (isStandalone) {
      return; // App is already installed
    }

    // Handle the install prompt event
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed the prompt before
      const hasSeenPrompt = localStorage.getItem('pwa-install-prompt-seen');
      const lastPromptTime = localStorage.getItem('pwa-install-prompt-time');
      const now = Date.now();
      
      // Show prompt if never seen or if more than 7 days have passed
      if (!hasSeenPrompt || (lastPromptTime && now - parseInt(lastPromptTime) > 7 * 24 * 60 * 60 * 1000)) {
        setTimeout(() => setShowInstallDialog(true), 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, check if should show instructions
    if (isIOSDevice && !isStandalone) {
      const hasSeenIOSInstructions = localStorage.getItem('ios-install-instructions-seen');
      if (!hasSeenIOSInstructions) {
        setTimeout(() => setShowIOSInstructions(true), 5000); // Show after 5 seconds
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowSuccessSnackbar(true);
        localStorage.setItem('pwa-installed', 'true');
      }
      
      setDeferredPrompt(null);
      setShowInstallDialog(false);
    } catch (error) {
      console.error('Error installing PWA:', error);
    }
  };

  const handleDialogClose = () => {
    setShowInstallDialog(false);
    localStorage.setItem('pwa-install-prompt-seen', 'true');
    localStorage.setItem('pwa-install-prompt-time', Date.now().toString());
  };

  const handleIOSInstructionsClose = () => {
    setShowIOSInstructions(false);
    localStorage.setItem('ios-install-instructions-seen', 'true');
  };

  return (
    <>
      {/* Android/Desktop Install Dialog */}
      <Dialog
        open={showInstallDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <FuseSvgIcon color="primary">material-outline:install_mobile</FuseSvgIcon>
              <Typography variant="h6">{t('PWA_INSTALL_TITLE')}</Typography>
            </Box>
            <IconButton onClick={handleDialogClose} size="small">
              <FuseSvgIcon size={20}>material-outline:close</FuseSvgIcon>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t('PWA_INSTALL_DESCRIPTION')}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 2 }}>
            <Typography component="li" variant="body2" gutterBottom>
              {t('PWA_INSTALL_BENEFIT_HOME_SCREEN')}
            </Typography>
            <Typography component="li" variant="body2" gutterBottom>
              {t('PWA_INSTALL_BENEFIT_OFFLINE')}
            </Typography>
            <Typography component="li" variant="body2" gutterBottom>
              {t('PWA_INSTALL_BENEFIT_NOTIFICATIONS')}
            </Typography>
            <Typography component="li" variant="body2" gutterBottom>
              {t('PWA_INSTALL_BENEFIT_FULLSCREEN')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="inherit">
            {t('PWA_INSTALL_NOT_NOW')}
          </Button>
          <Button 
            onClick={handleInstallClick} 
            variant="contained" 
            color="primary"
            startIcon={<FuseSvgIcon size={20}>material-outline:android</FuseSvgIcon>}
          >
            {t('PWA_INSTALL_BUTTON')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* iOS Instructions Dialog */}
      <Dialog
        open={showIOSInstructions}
        onClose={handleIOSInstructionsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <FuseSvgIcon color="primary">material-outline:phone_iphone</FuseSvgIcon>
              <Typography variant="h6">{t('PWA_INSTALL_IOS_TITLE')}</Typography>
            </Box>
            <IconButton onClick={handleIOSInstructionsClose} size="small">
              <FuseSvgIcon size={20}>material-outline:close</FuseSvgIcon>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t('PWA_INSTALL_IOS_DESCRIPTION')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              1. {t('PWA_INSTALL_IOS_STEP_1')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              2. {t('PWA_INSTALL_IOS_STEP_2')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              3. {t('PWA_INSTALL_IOS_STEP_3')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {t('PWA_INSTALL_IOS_NOTE')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIOSInstructionsClose} variant="contained" color="primary">
            {t('PWA_INSTALL_IOS_GOT_IT')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {t('PWA_INSTALL_SUCCESS_MESSAGE')}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PWAInstallPrompt;